/* Source: /tmp/jacbuilder-deploy-scratch/deploy-8asygg26/features/graph/graph_layout.jac */
import {__jacJsx, __jacSpawn} from "@jac/runtime";
import { kind_order } from "./graph_helpers.js";
import { _jac } from "@jac/prelude";
function start_layout(nodes, edges, width, height, left = 0.0) {
  let members;
  let radius;
  let angle;
  let count = nodes.length;
  let cx = ((width + left) / 2);
  let cy = (height / 2);
  let order = kind_order();
  let rings = order.filter(k => (nodes.filter(n => (n.kind === k)).map(n => n).length > 0)).map(k => k);
  for (const n of nodes) {
    if (!rings.includes(n.kind)) {
      rings.push(n.kind);
    }
  }
  let step = ((Math.min((width - left), height) / 2) / (rings.length + 1));
  let xs = [];
  let ys = [];
  let index_of = {};
  for (const ring of _jac.builtin.range(rings.length)) {
    members = nodes.filter(n => (n.kind === rings.at(ring))).map(n => n);
    radius = (((ring > 0) || (members.length > 1)) ? (step * ring) : 0.0);
    for (const j of _jac.builtin.range(members.length)) {
      angle = ((((2 * Math.PI) * j) / members.length) + (ring * 0.7));
      index_of[members.at(j).id] = xs.length;
      xs.push((cx + (radius * Math.cos(angle))));
      ys.push((cy + (radius * Math.sin(angle))));
    }
  }
  let ids = _jac.builtin.range(count).map(i => "");
  for (const n of nodes) {
    _jac.poly.setitem(ids, index_of[n.id], n.id);
  }
  let links = edges.filter(e => (((e.from_id in index_of) && (e.to_id in index_of)) && (e.from_id !== e.to_id))).map(e => [index_of[e.from_id], index_of[e.to_id]]);
  return {"ids": ids, "index": index_of, "xs": xs, "ys": ys, "links": links, "width": width, "height": height, "left": left, "k": (Math.sqrt((((width - left) * height) / Math.max(count, 1))) * 0.45), "temperature": (width / 8), "steps": 0, "drag": (-1)};
}
function layout_step(sim) {
  let ddx;
  let ddy;
  let dist2;
  let push;
  let pull;
  let mx;
  let my;
  let moved;
  let limit;
  let xs = sim["xs"];
  let ys = sim["ys"];
  let count = xs.length;
  let k = sim["k"];
  let width = sim["width"];
  let height = sim["height"];
  let left = sim["left"];
  let cx = ((width + left) / 2);
  let cy = (height / 2);
  let margin = 32.0;
  let dxs = _jac.builtin.range(count).map(i => 0.0);
  let dys = _jac.builtin.range(count).map(i => 0.0);
  for (const a of _jac.builtin.range(count)) {
    for (const b of _jac.builtin.range((a + 1), count)) {
      ddx = (xs.at(a) - xs.at(b));
      ddy = (ys.at(a) - ys.at(b));
      dist2 = ((ddx * ddx) + (ddy * ddy));
      if ((dist2 < 0.01)) {
        ddx = (0.1 * (a - b));
        ddy = 0.1;
        dist2 = ((ddx * ddx) + (ddy * ddy));
      }
      push = ((k * k) / dist2);
      dxs[a] += (ddx * push);
      dys[a] += (ddy * push);
      dxs[b] -= (ddx * push);
      dys[b] -= (ddy * push);
    }
  }
  for (const link of sim["links"]) {
    let a = link[0];
    let b = link[1];
    ddx = (xs.at(a) - xs.at(b));
    ddy = (ys.at(a) - ys.at(b));
    pull = (Math.sqrt(((ddx * ddx) + (ddy * ddy))) / k);
    dxs[a] -= (ddx * pull);
    dys[a] -= (ddy * pull);
    dxs[b] += (ddx * pull);
    dys[b] += (ddy * pull);
  }
  let temperature = sim["temperature"];
  for (const i of _jac.builtin.range(count)) {
    if ((i === sim["drag"])) {
      continue;
    }
    mx = (dxs.at(i) - ((xs.at(i) - cx) * 0.6));
    my = (dys.at(i) - ((ys.at(i) - cy) * 0.6));
    moved = Math.sqrt(((mx * mx) + (my * my)));
    if ((moved > 0)) {
      limit = Math.min(moved, temperature);
      _jac.poly.setitem(xs, i, Math.max((left + margin), Math.min((width - margin), (xs.at(i) + ((mx / moved) * limit)))));
      _jac.poly.setitem(ys, i, Math.max(margin, Math.min((height - margin), (ys.at(i) + ((my / moved) * limit)))));
    }
  }
  sim["temperature"] = (temperature * 0.95);
  sim["steps"] = (sim["steps"] + 1);
}
function layout_moving(sim) {
  return ((sim["temperature"] > 0.5) || (sim["drag"] >= 0));
}
function layout_drag(sim, slot, x, y) {
  let xs = sim["xs"];
  let ys = sim["ys"];
  let margin = 32.0;
  _jac.poly.setitem(xs, slot, Math.max((sim["left"] + margin), Math.min((sim["width"] - margin), x)));
  _jac.poly.setitem(ys, slot, Math.max(margin, Math.min((sim["height"] - margin), y)));
  sim["drag"] = slot;
  sim["temperature"] = Math.max(sim["temperature"], 12.0);
  sim["steps"] = (sim["steps"] + 1);
}
function layout_positions(sim) {
  let positions = {};
  let ids = sim["ids"];
  let xs = sim["xs"];
  let ys = sim["ys"];
  for (const i of _jac.builtin.range(ids.length)) {
    positions[ids.at(i)] = [xs.at(i), ys.at(i)];
  }
  return positions;
}
function layout_graph(nodes, edges, width, height) {
  let sim = start_layout(nodes, edges, width, height);
  let iterations = ((nodes.length <= 150) ? 150 : 60);
  for (const _ of _jac.builtin.range(iterations)) {
    layout_step(sim);
  }
  return layout_positions(sim);
}
function neighborhood(edges, node_id) {
  if ((node_id === "")) {
    return [];
  }
  let found = [node_id];
  for (const e of edges) {
    if (((e.from_id === node_id) && !found.includes(e.to_id))) {
      found.push(e.to_id);
    }
    if (((e.to_id === node_id) && !found.includes(e.from_id))) {
      found.push(e.from_id);
    }
  }
  return found;
}
export {layout_drag, layout_graph, layout_moving, layout_positions, layout_step, neighborhood, start_layout};
//# sourceMappingURL=graph_layout.js.map
